import {
  Button,
  Card, Col, Form, Row
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { useStateMachine } from "little-state-machine";
import {
  Divider, ErrorMessages, IconButton, IconButtonType, Spinner
} from "@whitespace/whitespace-ui";
import { UserIcon } from "components/UserIcon/UserIcon";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import ICreateUserRequest from "interfaces/requests/ICreateUserRequest";
import { yupResolver } from "@hookform/resolvers/yup";
import { ariaInvalidCheck, normaliseInteger } from "@whitespace/whitespace-utils";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useEffect, useState } from "react";
import { mapUserResponseItemToUser } from "mappers/mapUserResponseItemToUser/mapUserResponseItemToUser";
import IUserResponseItem from "interfaces/responses/IUserResponseItem";
import { convertToInternationalNumber } from "modules/phoneUtils";
import { validateInternationalPhoneNumber } from "modules/regExHelpers";
import Groups from "components/Groups/Groups";
import AmplifyError from "components/AmplifyError/AmplifyError";

import "./CreateUser.scss";

const CreateUser = () => {
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateSelectedUser
  });

  const {
    isSending, errors: apiErrors, data, sendRequest, resetState, statusCode
  } = useAmplifyREST();

  const [groupsSelected, setGroupsSelected] = useState<string[]>([]);
  const [createButtonDisabled, setCreateButtonDisabled] = useState<boolean>(true);
  const [getGroupsIsSending, setGetGroupsIsSending] = useState(false);
  const [getGroupsErrors, setGetGroupsErrors] = useState<string[]>([]);

  const schema = yup.object().shape({
    name: yup.string().trim()
      .required("Full Name is required"),
    email: yup.string().trim()
      .required("Email Address is required")
      .email("Invalid Email Address"),
    phone: yup.string().trim()
      .test(
        "valid international number",
        "Invalid Mobile Number",
        (val :string | undefined) => val!.length === 0 || validateInternationalPhoneNumber(convertToInternationalNumber(val!))
      )

  });

  const fullErrorList = apiErrors || getGroupsErrors;
  const areAnySending = isSending || getGroupsIsSending;

  const {
    formState: { errors }, register, handleSubmit, getValues, setValue
  } = useForm<ICreateUserRequest>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onBlur"
  });

  const canFormBeSubmitted = () => {
    setCreateButtonDisabled(
      !(groupsSelected.length > 0
    && getValues("name").length > 0
    && getValues("email").length > 0)
    );
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [groupsSelected]);

  const handleGroupSelectionChange = (updatedGroupsSelected: string[]) => {
    setGroupsSelected([]);
    setGroupsSelected(updatedGroupsSelected);
  };

  useEffect(() => {
    if (data && statusCode === 201) {
      const createdUser = mapUserResponseItemToUser(data as IUserResponseItem, []);
      actions.updateSelectedUser(createdUser);
      navigate("/manageuser");
    }
  }, [data, statusCode]);

  useDeepCompareEffect(() => {
    if (errors && Object.keys(errors).length !== 0) {
      resetState();
    }
  }, [errors]);

  const handleCreateUserClick = (request : ICreateUserRequest) => {
    request.enabled = true;
    request.phone = convertToInternationalNumber(request.phone);
    request.groups = groupsSelected;
    resetState();
    sendRequest("users", "POST", request);
  };

  return (
    <>
      {fullErrorList && <AmplifyError errors={fullErrorList} />}
      <Card>
        <Card.Header>
          <h1>
            Create New User
            <Spinner animation="grow" visible={areAnySending} spinnerText="Loading" variant="info" />
          </h1>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form autoComplete="off" onSubmit={handleSubmit(handleCreateUserClick)}>
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small create-user">
              <Col className="d-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={createButtonDisabled || isSending}
                  aria-disabled={createButtonDisabled || isSending}
                  type="submit"
                  spin={isSending}
                >
                  Create
                </IconButton>
                <Button variant="secondary" onClick={() => navigate("/users")}>
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row className="d-flex top-margin-small bottom-margin-small">
              <Col xs={12} lg={4}>
                <div className="user-section">
                  <Row className="mb-2">
                    <Form.Group controlId="name">
                      <Form.Label className="horiz-form-label">Full Name:</Form.Label>
                      <Form.Control
                        {...register("name")}
                        type="text"
                        role="textbox"
                        aria-required="true"
                        aria-invalid={ariaInvalidCheck("name", errors, null)}
                        maxLength={100}
                        onKeyUp={() => canFormBeSubmitted()}
                      />
                      <ErrorMessages fieldName="name" errors={errors} validationFailures={null} />
                    </Form.Group>
                  </Row>
                  <Row className="mb-2">
                    <Form.Group controlId="email">
                      <Form.Label className="horiz-form-label">Email Address:</Form.Label>
                      <Form.Control
                        {...register("email")}
                        type="text"
                        role="textbox"
                        aria-required="true"
                        aria-invalid={ariaInvalidCheck("email", errors, null)}
                        maxLength={100}
                        onKeyUp={() => canFormBeSubmitted()}
                      />
                      <ErrorMessages fieldName="email" errors={errors} validationFailures={null} />
                    </Form.Group>
                  </Row>
                  <Row className="mb-2">
                    <Form.Group controlId="phone">
                      <Form.Label className="horiz-form-label">Mobile Number:</Form.Label>
                      <div>
                        <div className="d-flex align-items-center">
                          <span className="d-inline-flex horiz-form-label mb-0 pe-1">+44</span>
                          <Form.Control
                            {...register("phone")}
                            type="text"
                            role="textbox"
                            className="d-inline-flex"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck("phone", errors, null)}
                            maxLength={13}
                            onChange={e => { e.target.value = normaliseInteger(e.target.value); }}

                          />
                        </div>
                        <ErrorMessages fieldName="phone" errors={errors} validationFailures={null} />

                      </div>

                    </Form.Group>
                  </Row>
                  <Row className="mb-2 mt-3">
                    <Col>
                      <Form.Group controlId="isPortalAdminUser" className="d-flex align-items-center">
                        <span className="me-3"><UserIcon isFederatedUser={false} isPortalAdminUser /></span>
                        <Form.Label className="d-inline-flex horiz-form-label w-auto mb-0 me-3">Portal Admin User</Form.Label>
                        <Form.Check
                          {...register("admin")}
                          data-testid="online-switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2"
                          type="switch"
                          aria-required="false"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={12} lg={8}>
                <h2 className="user-groups-header">User Group Access</h2>

                <div className="user-section">
                  <Groups
                    groupsSelected={[]}
                    handleGroupSelectionChange={args => handleGroupSelectionChange(args)}
                    handleErrors={setGetGroupsErrors}
                    handleIsSending={setGetGroupsIsSending}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};
export default CreateUser;
